import { Divider, Image, Space, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, ScopeButton } from '../components/buttons';
import { BlackSpan, GraySpan } from '../components/formSpans';
import { IconEdit, IconEye } from '../users/icons';
import { AccountType, resolveAccountType } from '../users/types';
import type { AccountLicence, AccountProfileUser } from './types';

type ProfileCardProps = {
  selectedAccount?: AccountProfileUser;
  currentUser?: AccountProfileUser;
  profilePicture: string;
  defaultAvatarSrc: string;
  openEditImageWindow: (mode?: 'preview' | 'edit') => void;
  renderUserSubscription: () => ReactNode;
  renderRole: (content: string) => ReactNode;
  getFullPhoneNumber: () => string;
  redirectPage: (url: string) => void;
  openEditDetails: () => void;
  userLicence?: AccountLicence;
  pricingPageLink?: string;
};

const getImageSrc = (picture: string) => {
  if (picture.startsWith('data:')) return picture;
  if (picture.startsWith('http')) return picture;
  if (/\.(svg|png|jpe?g|gif|webp|ico)$/i.test(picture)) return picture;
  return `data:image/png;base64,${picture}`;
};

export const ProfileCard = ({
  selectedAccount,
  currentUser,
  profilePicture,
  defaultAvatarSrc,
  openEditImageWindow,
  renderUserSubscription,
  renderRole,
  getFullPhoneNumber,
  redirectPage,
  openEditDetails,
  userLicence,
  pricingPageLink = '/pricing',
}: ProfileCardProps) => {
  const { t } = useTranslation();
  const profilePictureAlt = t('users.profile-picture-alt', {
    name: `${selectedAccount?.firstName ?? ''} ${selectedAccount?.lastName ?? ''}`.trim(),
  });
  const isCurrentUser = !selectedAccount?.userUuid
    ? !!currentUser?.userUuid
    : selectedAccount.userUuid === currentUser?.userUuid;

  return (
    <div className="account-profile-card">
      {isCurrentUser ? (
        <div className="account-profile-avatar-wrap">
          <div className="profile-image-hover-container">
            <Image
              height={150}
              width={150}
              className="profile-rounded-image"
              preview={false}
              alt={profilePictureAlt}
              src={getImageSrc(profilePicture)}
              style={{ width: '100%', height: '100%', margin: 0 }}
            />
            {profilePicture !== defaultAvatarSrc && (
              <Space className="profile-image-hover-icons">
                <IconButton
                  type="default"
                  className="icon-button-hover"
                  aria-label={t('common.preview')}
                  icon={<IconEye aria-hidden width={23} height={23} />}
                  onClick={() => openEditImageWindow('preview')}
                />
                <IconButton
                  type="default"
                  className="icon-button-hover"
                  aria-label={t('common.edit')}
                  icon={<IconEdit aria-hidden width={16} height={16} />}
                  onClick={() => openEditImageWindow('edit')}
                />
              </Space>
            )}
          </div>
        </div>
      ) : (
        <Image
          height={150}
          width={150}
          className="profile-rounded-image"
          preview={
            profilePicture === defaultAvatarSrc
              ? false
              : { destroyOnClose: true, toolbarRender: () => null }
          }
          alt={profilePictureAlt}
          src={getImageSrc(profilePicture)}
        />
      )}
      <Typography
        style={{
          fontSize: 18,
          fontWeight: 400,
          lineHeight: '24px',
          textAlign: 'center',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: 2,
        }}
      >
        {`${selectedAccount?.firstName ?? ''} ${selectedAccount?.lastName ?? ''}`}
      </Typography>
      {isCurrentUser && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {renderUserSubscription()}
        </div>
      )}
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 400,
          lineHeight: '18px',
          textAlign: 'center',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: 10,
        }}
      >
        {selectedAccount?.email}
      </Typography>
      <div style={{ margin: '0 36%' }}>
        {selectedAccount?.accountType && renderRole(selectedAccount.accountType)}
      </div>
      <Divider />
      <Space direction="vertical" style={{ marginBottom: 20 }}>
        {selectedAccount?.companyName && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('signup.company-name')}</GraySpan>
            <BlackSpan>{selectedAccount.companyName}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.phoneNumber && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('contacts.phone')}</GraySpan>
            <BlackSpan>{getFullPhoneNumber()}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.website && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('common.website')}</GraySpan>
            <BlackSpan>{selectedAccount.website}</BlackSpan>
          </Space>
        )}
        {selectedAccount?.timezone && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <GraySpan>{t('common.timezone')}</GraySpan>
            <BlackSpan>{selectedAccount.timezone}</BlackSpan>
          </Space>
        )}
      </Space>
      <Space direction="vertical" style={{ width: '100%' }}>
        {isCurrentUser &&
          resolveAccountType(selectedAccount?.accountType) === AccountType.OWNER &&
          String(userLicence?.licenseType ?? '').toUpperCase() === 'TRIAL' &&
          String(userLicence?.status ?? '').toUpperCase() !== 'UNLIMITED' && (
            <ScopeButton
              type="primary"
              width="100%"
              height="32px"
              onClick={() => redirectPage(pricingPageLink)}
            >
              {t('common.pricing-plans')}
            </ScopeButton>
          )}
        {isCurrentUser && (
          <ScopeButton type="default" width="100%" height="32px" onClick={openEditDetails}>
            {t('common.edit')}
          </ScopeButton>
        )}
      </Space>
    </div>
  );
};
